import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Calendar, Clock, Users, MapPin, ArrowRight, Star, Award, BookOpen, User, X, FileImage } from 'lucide-react';
import { useAuth } from '../App';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const HomePage = () => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [selectedPoster, setSelectedPoster] = useState(null);
  const { user } = useAuth();
  
  // Date selection modal for multi-day events
  const [dateSelectionModal, setDateSelectionModal] = useState(null); // {event, availableDates: [], selectedDates: []}
  
  // Filter states
  const [filters, setFilters] = useState({
    date: '',
    month: '',
    type: ''
  });

  useEffect(() => {
    fetchEvents();
  }, []);

  const fetchEvents = async () => {
    try {
      const response = await axios.get(`${API}/events`);
      // Filter only future events (including today) + date_tbd events
      const now = new Date();
      now.setHours(0, 0, 0, 0); // Start of today
      const futureEvents = response.data.filter(event => {
        // Always include date_tbd events
        if (event.date_tbd) return true;
        return new Date(event.start_date) >= now;
      });
      setEvents(futureEvents);
    } catch (error) {
      console.error('Error fetching events:', error);
      toast.error('Errore nel caricamento degli eventi');
    } finally {
      setLoading(false);
    }
  };

  // Sort events: dated events chronologically first, then date_tbd at the end
  const sortEventsByDate = (eventsArray) => {
    return [...eventsArray].sort((a, b) => {
      // date_tbd events go to the end
      if (a.date_tbd && !b.date_tbd) return 1;
      if (!a.date_tbd && b.date_tbd) return -1;
      if (a.date_tbd && b.date_tbd) return 0;
      const dateA = new Date(a.start_date);
      const dateB = new Date(b.start_date);
      return dateA - dateB;
    });
  };

  // Filter events based on criteria
  const filterEvents = (eventsArray) => {
    return eventsArray.filter(event => {
      // date_tbd events: always show unless a specific date/month filter is active
      if (event.date_tbd) {
        if (filters.date || filters.month) return false;
        if (filters.type && event.type !== filters.type) return false;
        return true;
      }

      const eventStartDate = new Date(event.start_date);
      const eventMonth = eventStartDate.getMonth(); // 0-11
      const eventYear = eventStartDate.getFullYear();
      
      // Date filter (check if filter date falls within event range)
      if (filters.date) {
        const filterDate = new Date(filters.date);
        const eventEndDate = new Date(event.end_date);
        if (filterDate < eventStartDate || filterDate > eventEndDate) {
          return false;
        }
      }
      
      // Month filter
      if (filters.month) {
        const [filterYear, filterMonth] = filters.month.split('-');
        if (eventYear !== parseInt(filterYear) || eventMonth !== parseInt(filterMonth) - 1) {
          return false;
        }
      }
      
      // Type filter
      if (filters.type && event.type !== filters.type) {
        return false;
      }
      
      return true;
    });
  };

  // Separate events into regular and training
  const categorizeEvents = (eventsArray) => {
    const sorted = sortEventsByDate(eventsArray);
    const filtered = filterEvents(sorted);
    
    const regular = filtered.filter(e => e.type !== 'formazione_insegnanti');
    const training = filtered.filter(e => e.type === 'formazione_insegnanti');
    
    return { regular, training };
  };

  const { regular: regularEvents, training: trainingEvents } = categorizeEvents(events);

  const clearFilters = () => {
    setFilters({
      date: '',
      month: '',
      type: ''
    });
  };

  // Generate all dates between start and end
  const getEventDates = (startDate, endDate) => {
    const dates = [];
    const start = new Date(startDate);
    const end = new Date(endDate);
    const current = new Date(start);
    while (current <= end) {
      dates.push(current.toISOString().split('T')[0]);
      current.setDate(current.getDate() + 1);
    }
    return dates;
  };

  const isMultiDayEvent = (event) => {
    return event.start_date && event.end_date && event.start_date !== event.end_date && !event.date_tbd;
  };

  const handleEnroll = async (eventId) => {
    const event = events.find(e => e.id === eventId);
    if (!event) return;

    // If multi-day event OR event has dance_analysis, show enrollment modal
    if (isMultiDayEvent(event) || event.dance_analysis) {
      const availableDates = isMultiDayEvent(event) ? getEventDates(event.start_date, event.end_date) : [];
      setDateSelectionModal({
        event,
        availableDates,
        selectedDates: [...availableDates],
        danceAnalysisRequested: false,
        danceAnalysisDiscipline: ''
      });
      return;
    }

    // Single day without dance_analysis: enroll directly
    try {
      await axios.post(`${API}/events/${eventId}/enroll`, {});
      toast.success('Richiesta di iscrizione inviata! In attesa di approvazione da parte dell\'admin.');
      fetchEvents();
    } catch (error) {
      const message = error.response?.data?.detail || 'Errore durante l\'iscrizione';
      if (error.response?.status === 403 && message.includes('formazione degli insegnanti')) {
        toast.error('Evento riservato alla formazione degli insegnanti', {
          duration: 4000,
          description: 'Solo i teacher e i formatori possono iscriversi a questo evento.'
        });
      } else {
        toast.error(message);
      }
    }
  };

  const confirmEnrollWithDates = async () => {
    if (!dateSelectionModal) return;
    const { event, selectedDates, danceAnalysisRequested, danceAnalysisDiscipline } = dateSelectionModal;
    
    // Validate dates for multi-day events
    if (isMultiDayEvent(event) && selectedDates.length === 0) {
      toast.error('Seleziona almeno una data');
      return;
    }

    // Validate dance analysis discipline if requested
    if (danceAnalysisRequested && (!danceAnalysisDiscipline || !danceAnalysisDiscipline.trim())) {
      toast.error('Indica la disciplina per la Dance Analysis');
      return;
    }

    try {
      const payload = {};
      if (isMultiDayEvent(event)) {
        payload.selected_dates = selectedDates;
      }
      if (danceAnalysisRequested) {
        payload.dance_analysis_requested = true;
        payload.dance_analysis_discipline = danceAnalysisDiscipline.trim();
      }
      await axios.post(`${API}/events/${event.id}/enroll`, payload);
      const dateMsg = isMultiDayEvent(event) ? ` per ${selectedDates.length} ${selectedDates.length === 1 ? 'data' : 'date'}` : '';
      toast.success(`Iscrizione inviata${dateMsg}! In attesa di approvazione.`);
      setDateSelectionModal(null);
      fetchEvents();
    } catch (error) {
      const message = error.response?.data?.detail || 'Errore durante l\'iscrizione';
      toast.error(message);
    }
  };

  const toggleDate = (date) => {
    if (!dateSelectionModal) return;
    const { selectedDates } = dateSelectionModal;
    if (selectedDates.includes(date)) {
      setDateSelectionModal({...dateSelectionModal, selectedDates: selectedDates.filter(d => d !== date)});
    } else {
      setDateSelectionModal({...dateSelectionModal, selectedDates: [...selectedDates, date].sort()});
    }
  };

  const toggleAllDates = () => {
    if (!dateSelectionModal) return;
    const { availableDates, selectedDates } = dateSelectionModal;
    if (selectedDates.length === availableDates.length) {
      setDateSelectionModal({...dateSelectionModal, selectedDates: []});
    } else {
      setDateSelectionModal({...dateSelectionModal, selectedDates: [...availableDates]});
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('it-IT', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Format date range for events
  const formatDateRange = (startDate, endDate, dateTbd) => {
    if (dateTbd) return 'DATA DA DEFINIRE';
    if (!startDate || !endDate) return 'DATA DA DEFINIRE';
    
    const start = new Date(startDate);
    const end = new Date(endDate);
    
    if (isNaN(start.getTime()) || isNaN(end.getTime())) return 'DATA DA DEFINIRE';
    
    // If same day, show only one date
    if (startDate === endDate) {
      return start.toLocaleDateString('it-IT', { 
        weekday: 'long', 
        year: 'numeric', 
        month: 'long', 
        day: 'numeric'
      });
    }
    
    // Different days - show range
    const startFormatted = start.toLocaleDateString('it-IT', { 
      day: 'numeric', 
      month: 'long'
    });
    const endFormatted = end.toLocaleDateString('it-IT', { 
      day: 'numeric', 
      month: 'long', 
      year: 'numeric'
    });
    
    return `${startFormatted} - ${endFormatted}`;
  };

  const getEventIcon = (type) => {
    switch (type) {
      case 'stage': return <Star className="w-5 h-5" />;
      case 'lecture': return <BookOpen className="w-5 h-5" />;
      case 'online': return <Award className="w-5 h-5" />;
      default: return <Calendar className="w-5 h-5" />;
    }
  };

  const getEventColor = (type) => {
    switch (type) {
      case 'stage': return 'bg-gradient-to-r from-purple-500 to-pink-500';
      case 'lecture': return 'bg-gradient-to-r from-blue-500 to-indigo-500';
      case 'online': return 'bg-gradient-to-r from-green-500 to-teal-500';
      default: return 'bg-gradient-to-r from-gray-500 to-gray-600';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600" data-testid="loading-spinner"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-red-600 via-red-700 to-red-800 text-white py-20 overflow-hidden">
        <div className="absolute inset-0 bg-black opacity-20"></div>
        <div className="absolute inset-0">
          <div className="absolute top-10 left-10 w-20 h-20 border border-white/20 rounded-full animate-pulse"></div>
          <div className="absolute top-40 right-20 w-32 h-32 border border-white/10 rounded-full animate-pulse" style={{animationDelay: '1s'}}></div>
          <div className="absolute bottom-20 left-1/4 w-16 h-16 border border-white/15 rounded-full animate-pulse" style={{animationDelay: '2s'}}></div>
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="font-display text-5xl md:text-7xl font-bold mb-6 animate-fadeIn" data-testid="hero-title">
            DANCE ACADEMY
          </h1>
          <p className="text-xl md:text-2xl mb-8 max-w-4xl mx-auto leading-relaxed animate-fadeIn" data-testid="hero-subtitle">
            La prima piattaforma dedicata alla formazione degli Atleti di Danza per ogni categoria, disciplina e classe.
            <br /><br />
            Scopri Stage, Lecture e Incontri Formativi con i Migliori Insegnanti.
            <br /><br />
            <span className="text-lg md:text-xl font-medium">
              Ideato con il contributo intellettuale<br />
              del Presidente CIBS Edilio Pagano e del Vice Presidente Vicario CIBS Giancarlo Tarabella.
              <br /><br />
              Sviluppato da LMKT e Gestito da Valeria Tria
            </span>
          </p>
          
          {!user && (
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center animate-slideInRight">
              <Link to="/register">
                <Button 
                  size="lg" 
                  className="bg-white text-red-600 hover:bg-gray-100 font-semibold px-8 py-3 rounded-full shadow-lg hover:shadow-xl transition-all duration-300 btn-primary"
                  data-testid="hero-register-button"
                >
                  Inizia Ora
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
              <Link to="/login">
                <Button 
                  variant="outline" 
                  size="lg" 
                  className="border-white text-white hover:bg-white hover:text-red-600 font-semibold px-8 py-3 rounded-full transition-all duration-300"
                  data-testid="hero-login-button"
                >
                  Accedi
                </Button>
              </Link>
            </div>
          )}
        </div>
      </section>

      {/* Events Section */}
      <section className="py-16 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="font-display text-4xl font-bold text-gray-900 mb-4" data-testid="events-section-title">
              Prossimi Eventi
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Scopri i nostri stage, lecture e incontri formativi online
            </p>
          </div>

          {/* Filters */}
          <div className="mb-8 bg-white rounded-lg shadow-md p-6">
            <h3 className="text-lg font-semibold mb-4 text-gray-900">Filtra Eventi</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Data Specifica</label>
                <input
                  type="date"
                  value={filters.date}
                  onChange={(e) => setFilters({...filters, date: e.target.value, month: ''})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Mese</label>
                <input
                  type="month"
                  value={filters.month}
                  onChange={(e) => setFilters({...filters, month: e.target.value, date: ''})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Tipologia</label>
                <select
                  value={filters.type}
                  onChange={(e) => setFilters({...filters, type: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">Tutte le tipologie</option>
                  <option value="stage">Stage</option>
                  <option value="lecture">Lecture</option>
                  <option value="online">Online</option>
                  <option value="formazione_insegnanti">Formazione Insegnanti</option>
                </select>
              </div>
            </div>
            
            {(filters.date || filters.month || filters.type) && (
              <div className="mt-4 flex justify-end">
                <Button
                  onClick={clearFilters}
                  variant="outline"
                  size="sm"
                  className="flex items-center"
                >
                  <X className="w-4 h-4 mr-1" />
                  Pulisci Filtri
                </Button>
              </div>
            )}
          </div>

          {events.length === 0 ? (
            <div className="text-center py-12" data-testid="no-events-message">
              <Calendar className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-gray-600 mb-2">Nessun evento programmato</h3>
              <p className="text-gray-500">Torna presto per vedere i prossimi eventi!</p>
            </div>
          ) : (
            <>
              {/* Regular Events */}
              {regularEvents.length > 0 && (
                <>
                  <h3 className="text-2xl font-bold text-gray-900 mb-6">Eventi Generali</h3>
                  <div className="responsive-grid mb-12" data-testid="events-grid">
                    {regularEvents.map((event) => {
                      const isEventFull = event.enrolled_count >= event.max_participants;
                      const isEnrollmentClosed = event.enrollment_closed;
                      const canEnroll = user && user.approved && !isEventFull && !isEnrollmentClosed;
                
                return (
                  <Card key={event.id} className="card-hover group relative overflow-hidden border-0 shadow-lg" data-testid={`event-card-${event.id}`}>
                    <div className={`absolute top-0 left-0 w-full h-2 ${getEventColor(event.type)}`}></div>
                    
                    <CardHeader className="pb-4">
                      <div className="flex items-start justify-between">
                        <div className="flex items-center space-x-2 mb-2">
                          <div className={`p-2 rounded-lg text-white ${getEventColor(event.type)}`}>
                            {getEventIcon(event.type)}
                          </div>
                          <Badge variant="secondary" className="text-xs uppercase tracking-wide" data-testid={`event-type-${event.id}`}>
                            {event.type}
                          </Badge>
                        </div>
                        
                        <div className="flex items-center space-x-1 text-sm text-gray-500" data-testid={`event-participants-${event.id}`}>
                          <Users className="w-4 h-4" />
                          <span>{event.enrolled_count}/{event.max_participants}</span>
                        </div>
                      </div>
                      
                      <CardTitle className="text-xl font-bold text-gray-900 group-hover:text-red-600 transition-colors" data-testid={`event-title-${event.id}`}>
                        {event.title}
                      </CardTitle>
                      
                      <CardDescription className="text-gray-600 line-clamp-3" data-testid={`event-description-${event.id}`}>
                        {event.description}
                      </CardDescription>
                    </CardHeader>
                    
                    <CardContent className="pt-0">
                      <div className="space-y-3 mb-6">
                        <div className="flex items-center space-x-2 text-sm text-gray-600" data-testid={`event-datetime-${event.id}`}>
                          <Clock className="w-4 h-4 text-red-500" />
                          <span>{formatDateRange(event.start_date, event.end_date, event.date_tbd)}</span>
                        </div>
                        
                        {event.location && (
                          <div className="flex items-center space-x-2 text-sm text-gray-600" data-testid={`event-location-${event.id}`}>
                            <MapPin className="w-4 h-4 text-red-500" />
                            <span>{event.location}</span>
                          </div>
                        )}
                        
                        {event.teacher_names && event.teacher_names.length > 0 && (
                          <div className="flex items-center space-x-2 text-sm text-gray-600" data-testid={`event-teachers-${event.id}`}>
                            <User className="w-4 h-4 text-red-500" />
                            <span>
                              {event.teacher_names.length === 1 ? 'Insegnante: ' : 'Insegnanti: '}
                              {event.teacher_names.join(', ')}
                            </span>
                          </div>
                        )}
                        
                        {isEventFull && (
                          <Badge variant="destructive" className="w-fit" data-testid={`event-full-badge-${event.id}`}>
                            Evento Completo
                          </Badge>
                        )}

                        {event.dance_analysis && (
                          <div className="flex items-center space-x-2 text-sm font-medium text-purple-700 bg-purple-50 rounded-lg px-3 py-2" data-testid={`event-dance-analysis-${event.id}`}>
                            <Star className="w-4 h-4 text-purple-500" />
                            <span>Durante l'evento sarà svolta la Dance Analysis</span>
                          </div>
                        )}
                      </div>
                      
                      {/* Poster button */}
                      {event.poster_url && (
                        <Button
                          onClick={() => setSelectedPoster(event)}
                          variant="outline"
                          size="sm"
                          className="w-full mt-2 flex items-center justify-center gap-2 border-purple-300 text-purple-700 hover:bg-purple-50"
                        >
                          <FileImage className="w-4 h-4" />
                          Locandina
                        </Button>
                      )}
                      
                      {user ? (
                        user.approved ? (
                          isEnrollmentClosed ? (
                            <div className="text-center py-3 bg-gray-50 rounded-lg border border-gray-200" data-testid={`enrollment-closed-${event.id}`}>
                              <p className="text-sm text-gray-600 font-medium">
                                Iscrizioni all'Evento chiuse. Per supporto, contattare l'assistenza.
                              </p>
                            </div>
                          ) : (
                          <Button
                            onClick={() => handleEnroll(event.id)}
                            disabled={isEventFull}
                            className={`w-full ${
                              isEventFull 
                                ? 'bg-gray-300 text-gray-500 cursor-not-allowed' 
                                : 'bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 text-white btn-primary'
                            }`}
                            data-testid={`enroll-button-${event.id}`}
                          >
                            {isEventFull ? 'Evento Completo' : 'Iscriviti'}
                          </Button>
                          )
                        ) : (
                          <div className="text-center py-3 bg-orange-50 rounded-lg border border-orange-200" data-testid={`approval-required-${event.id}`}>
                            <p className="text-sm text-orange-700 font-medium">
                              Account in attesa di approvazione
                            </p>
                          </div>
                        )
                      ) : (
                        isEnrollmentClosed ? (
                          <div className="text-center py-3 bg-gray-50 rounded-lg border border-gray-200" data-testid={`enrollment-closed-${event.id}`}>
                            <p className="text-sm text-gray-600 font-medium">
                              Iscrizioni all'Evento chiuse. Per supporto, contattare l'assistenza.
                            </p>
                          </div>
                        ) : (
                        <div className="flex space-x-2">
                          <Button 
                            variant="outline" 
                            size="sm" 
                            className="flex-1" 
                            onClick={() => setSelectedEvent(event)}
                            data-testid={`event-details-${event.id}`}
                          >
                            Dettagli
                          </Button>
                          <Link to="/login" className="flex-1">
                            <Button size="sm" className="w-full bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 text-white" data-testid={`login-to-enroll-${event.id}`}>
                              Accedi per iscriverti
                            </Button>
                          </Link>
                        </div>
                        )
                      )}
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </>
        )}
        
        {/* Past Events Button */}
        {(regularEvents.length > 0 || trainingEvents.length > 0) && (
          <div className="flex justify-center my-12">
            <Link to="/eventi-passati">
              <Button 
                className="bg-cyan-400 hover:bg-cyan-500 text-black font-semibold px-8 py-3 text-lg shadow-md hover:shadow-lg transition-all duration-200"
              >
                <Calendar className="w-5 h-5 mr-2" />
                EVENTI PASSATI
              </Button>
            </Link>
          </div>
        )}
        
        {/* Separator for Training Events */}
        {regularEvents.length > 0 && trainingEvents.length > 0 && (
          <div className="my-12">
            <div className="h-1 bg-gradient-to-r from-cyan-400 via-cyan-500 to-cyan-400 rounded-full"></div>
          </div>
        )}
        
        {/* Training Events */}
        {trainingEvents.length > 0 && (
          <>
            <h3 className="text-2xl font-bold text-gray-900 mb-2">Formazione Insegnanti</h3>
            <p className="text-gray-600 mb-6">Eventi dedicati alla formazione e aggiornamento professionale degli insegnanti</p>
            <div className="responsive-grid" data-testid="training-events-grid">
              {trainingEvents.map((event) => {
                const isEventFull = event.enrolled_count >= event.max_participants;
                const isEnrollmentClosed = event.enrollment_closed;
                const canEnroll = user && user.approved && !isEventFull && !isEnrollmentClosed;
                
                return (
                  <Card key={event.id} className="card-hover group relative overflow-hidden border-0 shadow-lg" data-testid={`event-card-${event.id}`}>
                    <div className={`absolute top-0 left-0 w-full h-2 ${getEventColor(event.type)}`}></div>
                    
                    <CardHeader className="pb-4">
                      <div className="flex items-start justify-between">
                        <div className="flex items-center space-x-2 mb-2">
                          <div className={`p-2 rounded-lg text-white ${getEventColor(event.type)}`}>
                            {getEventIcon(event.type)}
                          </div>
                          <Badge variant="secondary" className="text-xs uppercase tracking-wide bg-cyan-100 text-cyan-800" data-testid={`event-type-${event.id}`}>
                            Formazione Insegnanti
                          </Badge>
                        </div>
                      </div>
                      <CardTitle className="text-2xl font-bold text-gray-900 group-hover:text-red-600 transition-colors duration-200" data-testid={`event-title-${event.id}`}>
                        {event.title}
                      </CardTitle>
                      <CardDescription className="text-gray-600 line-clamp-2" data-testid={`event-description-${event.id}`}>
                        {event.description}
                      </CardDescription>
                    </CardHeader>
                    
                    <CardContent className="space-y-4">
                      <div className="space-y-3">
                        <div className="flex items-center space-x-2 text-sm font-medium text-gray-700" data-testid={`event-datetime-${event.id}`}>
                          <Calendar className="w-4 h-4 text-red-500" />
                          <span>{formatDateRange(event.start_date, event.end_date, event.date_tbd)}</span>
                        </div>
                        
                        <div className="flex items-center space-x-2 text-sm text-gray-600" data-testid={`event-capacity-${event.id}`}>
                          <Users className="w-4 h-4 text-red-500" />
                          <span>Max {event.max_participants} partecipanti</span>
                        </div>
                        
                        {event.location && (
                          <div className="flex items-center space-x-2 text-sm text-gray-600" data-testid={`event-location-${event.id}`}>
                            <MapPin className="w-4 h-4 text-red-500" />
                            <span>{event.location}</span>
                          </div>
                        )}
                        
                        {event.teacher_names && event.teacher_names.length > 0 && (
                          <div className="flex items-center space-x-2 text-sm text-gray-600" data-testid={`event-teachers-${event.id}`}>
                            <User className="w-4 h-4 text-red-500" />
                            <span>
                              {event.teacher_names.length === 1 ? 'Formatore: ' : 'Formatori: '}
                              {event.teacher_names.join(', ')}
                            </span>
                          </div>
                        )}
                        
                        {isEventFull && (
                          <Badge variant="destructive" className="w-fit" data-testid={`event-full-badge-${event.id}`}>
                            Evento Completo
                          </Badge>
                        )}

                        {event.dance_analysis && (
                          <div className="flex items-center space-x-2 text-sm font-medium text-purple-700 bg-purple-50 rounded-lg px-3 py-2" data-testid={`event-dance-analysis-${event.id}`}>
                            <Star className="w-4 h-4 text-purple-500" />
                            <span>Durante l'evento sarà svolta la Dance Analysis</span>
                          </div>
                        )}
                      </div>
                      
                      {/* Poster button */}
                      {event.poster_url && (
                        <Button
                          onClick={() => setSelectedPoster(event)}
                          variant="outline"
                          size="sm"
                          className="w-full mt-2 flex items-center justify-center gap-2 border-purple-300 text-purple-700 hover:bg-purple-50"
                        >
                          <FileImage className="w-4 h-4" />
                          Locandina
                        </Button>
                      )}
                      
                      {user ? (
                        canEnroll ? (
                          <Button 
                            onClick={() => handleEnroll(event.id)} 
                            className="w-full bg-gradient-to-r from-cyan-500 to-cyan-600 hover:from-cyan-600 hover:to-cyan-700 text-white shadow-lg hover:shadow-xl transition-all duration-200"
                            data-testid={`enroll-button-${event.id}`}
                          >
                            <ArrowRight className="w-4 h-4 mr-2" />
                            Iscriviti ora
                          </Button>
                        ) : (
                          isEnrollmentClosed ? (
                            <div className="text-center py-3 bg-gray-50 rounded-lg border border-gray-200" data-testid={`enrollment-closed-${event.id}`}>
                              <p className="text-sm text-gray-600 font-medium">
                                Iscrizioni all'Evento chiuse. Per supporto, contattare l'assistenza.
                              </p>
                            </div>
                          ) : (
                          <div className="text-center py-3 bg-orange-50 rounded-lg border border-orange-200" data-testid={`approval-required-${event.id}`}>
                            <p className="text-sm text-orange-700 font-medium">
                              {isEventFull ? 'Evento Completo' : 'Account in attesa di approvazione'}
                            </p>
                          </div>
                          )
                        )
                      ) : (
                        isEnrollmentClosed ? (
                          <div className="text-center py-3 bg-gray-50 rounded-lg border border-gray-200" data-testid={`enrollment-closed-${event.id}`}>
                            <p className="text-sm text-gray-600 font-medium">
                              Iscrizioni all'Evento chiuse. Per supporto, contattare l'assistenza.
                            </p>
                          </div>
                        ) : (
                        <div className="flex space-x-2">
                          <Button 
                            variant="outline" 
                            size="sm" 
                            className="flex-1" 
                            onClick={() => setSelectedEvent(event)}
                            data-testid={`event-details-${event.id}`}
                          >
                            Dettagli
                          </Button>
                          <Link to="/login" className="flex-1">
                            <Button size="sm" className="w-full bg-gradient-to-r from-cyan-500 to-cyan-600 hover:from-cyan-600 hover:to-cyan-700 text-white" data-testid={`login-to-enroll-${event.id}`}>
                              Accedi per iscriverti
                            </Button>
                          </Link>
                        </div>
                        )
                      )}
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </>
        )}
        
        {/* No results message */}
        {regularEvents.length === 0 && trainingEvents.length === 0 && events.length > 0 && (
          <div className="text-center py-12">
            <Calendar className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-600 mb-2">Nessun evento trovato</h3>
            <p className="text-gray-500">Prova a modificare i filtri di ricerca</p>
          </div>
        )}
      </>
    )}
  </div>
</section>

      {/* Features Section */}
      <section className="py-16 bg-gradient-to-r from-red-600 to-red-700 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="font-display text-4xl font-bold mb-4" data-testid="features-title">
              Perché Scegliere Dance Academy?
            </h2>
            <p className="text-xl opacity-90 max-w-2xl mx-auto">
              La piattaforma completa per la tua crescita artistica
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center" data-testid="feature-training">
              <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <Star className="w-8 h-8" />
              </div>
              <h3 className="text-xl font-semibold mb-2">Formazione di Qualità</h3>
              <p className="opacity-90">Stage e lecture con insegnanti professionali per migliorare le tue performance</p>
            </div>
            
            <div className="text-center" data-testid="feature-analysis">
              <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <BookOpen className="w-8 h-8" />
              </div>
              <h3 className="text-xl font-semibold mb-2">Match Analysis</h3>
              <p className="opacity-90">Ricevi feedback personalizzati per crescere e migliorare le tue tecniche</p>
            </div>
            
            <div className="text-center" data-testid="feature-certificates">
              <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <Award className="w-8 h-8" />
              </div>
              <h3 className="text-xl font-semibold mb-2">Certificazioni</h3>
              <p className="opacity-90">Ottieni attestati ufficiali per il tuo percorso formativo</p>
            </div>
          </div>
        </div>
      </section>

      {/* Event Details Modal */}
      {selectedEvent && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-2xl font-bold text-gray-900">
                  {selectedEvent.title}
                </h2>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setSelectedEvent(null)}
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>
              
              <div className="space-y-4">
                <div className="flex items-center space-x-2">
                  <Badge variant="outline" className="text-sm">
                    {selectedEvent.type}
                  </Badge>
                </div>

                <div className="space-y-3">
                  <div className="flex items-center space-x-2 text-gray-600">
                    <Calendar className="w-4 h-4 text-red-500" />
                    <span>{formatDateRange(selectedEvent.start_date, selectedEvent.end_date, selectedEvent.date_tbd)}</span>
                  </div>
                  
                  {selectedEvent.location && (
                    <div className="flex items-center space-x-2 text-gray-600">
                      <MapPin className="w-4 h-4 text-red-500" />
                      <span>{selectedEvent.location}</span>
                    </div>
                  )}
                  
                  <div className="flex items-center space-x-2 text-gray-600">
                    <Users className="w-4 h-4 text-red-500" />
                    <span>Max {selectedEvent.max_participants} partecipanti</span>
                  </div>
                  
                  {selectedEvent.teacher_names && selectedEvent.teacher_names.length > 0 && (
                    <div className="flex items-center space-x-2 text-gray-600">
                      <User className="w-4 h-4 text-red-500" />
                      <span>
                        {selectedEvent.teacher_names.length === 1 ? 'Insegnante: ' : 'Insegnanti: '}
                        {selectedEvent.teacher_names.join(', ')}
                      </span>
                    </div>
                  )}

                  {selectedEvent.external_teachers_text && selectedEvent.external_teachers_text.trim() && (
                    <div className="flex items-center space-x-2 text-gray-600">
                      <User className="w-4 h-4 text-blue-500" />
                      <span>
                        Altri Insegnanti/Relatori: {selectedEvent.external_teachers_text}
                      </span>
                    </div>
                  )}
                </div>

                <div className="border-t pt-4">
                  <h3 className="text-lg font-semibold mb-2">Descrizione</h3>
                  <div className="text-gray-700 whitespace-pre-wrap leading-relaxed">
                    {selectedEvent.description}
                  </div>
                </div>

                <div className="border-t pt-4 flex space-x-3">
                  <Link to="/register" className="flex-1">
                    <Button className="w-full bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 text-white">
                      Registrati per Partecipare
                    </Button>
                  </Link>
                  <Link to="/login" className="flex-1">
                    <Button variant="outline" className="w-full">
                      Accedi
                    </Button>
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Poster Modal */}
      {selectedPoster && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-hidden">
            <div className="p-4 border-b flex justify-between items-center">
              <h2 className="text-xl font-bold text-gray-900">
                Locandina - {selectedPoster.title}
              </h2>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setSelectedPoster(null)}
              >
                <X className="w-4 h-4" />
              </Button>
            </div>
            <div className="p-4 flex justify-center">
              {selectedPoster.poster_url && selectedPoster.poster_url.toLowerCase().endsWith('.pdf') ? (
                <iframe
                  src={selectedPoster.poster_url.startsWith('http') ? selectedPoster.poster_url : `${BACKEND_URL}${selectedPoster.poster_url}`}
                  title={`Locandina ${selectedPoster.title}`}
                  className="w-full h-[70vh] rounded-lg shadow-lg"
                  onError={(e) => {
                    e.target.style.display = 'none';
                    e.target.nextSibling.style.display = 'block';
                  }}
                />
              ) : (
                <img
                  src={selectedPoster.poster_url && selectedPoster.poster_url.startsWith('http') ? selectedPoster.poster_url : `${BACKEND_URL}${selectedPoster.poster_url}`}
                  alt={`Locandina ${selectedPoster.title}`}
                  className="max-w-full max-h-[70vh] object-contain rounded-lg shadow-lg"
                  onError={(e) => {
                    e.target.style.display = 'none';
                    e.target.nextSibling.style.display = 'block';
                  }}
                />
              )}
              <div className="hidden text-center text-gray-500 py-8">
                <FileImage className="w-16 h-16 mx-auto mb-4 text-gray-400" />
                <p>Impossibile caricare la locandina</p>
              </div>
            </div>
          </div>
        </div>
      )}
      {/* Date Selection Modal for multi-day events */}
      {dateSelectionModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50" data-testid="date-selection-modal">
          <div className="bg-white rounded-xl p-6 max-w-md w-full mx-4 shadow-2xl max-h-[80vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-gray-900">
                {isMultiDayEvent(dateSelectionModal.event) ? 'Iscrizione Evento' : 'Iscrizione Evento'}
              </h3>
              <button onClick={() => setDateSelectionModal(null)} className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <p className="text-sm text-gray-600 mb-1">
              <strong>{dateSelectionModal.event.title}</strong>
            </p>

            {/* Date selection - only for multi-day events */}
            {isMultiDayEvent(dateSelectionModal.event) && (
              <>
                <p className="text-xs text-gray-500 mb-4">
                  Seleziona le date a cui desideri partecipare
                </p>

                <div className="mb-3">
                  <button
                    onClick={toggleAllDates}
                    className="text-sm text-red-600 hover:text-red-700 font-medium"
                    data-testid="toggle-all-dates-btn"
                  >
                    {dateSelectionModal.selectedDates.length === dateSelectionModal.availableDates.length
                      ? 'Deseleziona tutte'
                      : 'Seleziona tutte'}
                  </button>
                </div>

                <div className="space-y-2 mb-4">
                  {dateSelectionModal.availableDates.map((date) => {
                    const isSelected = dateSelectionModal.selectedDates.includes(date);
                    const dateObj = new Date(date + 'T00:00:00');
                    const formatted = dateObj.toLocaleDateString('it-IT', {
                      weekday: 'long', day: 'numeric', month: 'long', year: 'numeric'
                    });
                    return (
                      <label
                        key={date}
                        className={`flex items-center p-3 rounded-lg border-2 cursor-pointer transition-all ${
                          isSelected
                            ? 'border-red-500 bg-red-50 text-red-900'
                            : 'border-gray-200 hover:border-gray-300 bg-white'
                        }`}
                        data-testid={`date-option-${date}`}
                      >
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => toggleDate(date)}
                          className="w-4 h-4 text-red-600 rounded mr-3"
                        />
                        <span className="text-sm font-medium capitalize">{formatted}</span>
                      </label>
                    );
                  })}
                </div>

                <div className="flex items-center justify-between mb-4">
                  <span className="text-sm text-gray-600">
                    {dateSelectionModal.selectedDates.length} di {dateSelectionModal.availableDates.length} date selezionate
                  </span>
                </div>
              </>
            )}

            {/* Dance Analysis option */}
            {dateSelectionModal.event.dance_analysis && (
              <div className="border-t pt-4 mb-4" data-testid="dance-analysis-section">
                <div className="flex items-center space-x-2 text-sm font-medium text-purple-700 bg-purple-50 rounded-lg px-3 py-2 mb-3">
                  <Star className="w-4 h-4 text-purple-500" />
                  <span>Durante l'evento sarà svolta la Dance Analysis</span>
                </div>
                <label className={`flex items-center p-3 rounded-lg border-2 cursor-pointer transition-all mb-3 ${
                  dateSelectionModal.danceAnalysisRequested
                    ? 'border-purple-500 bg-purple-50 text-purple-900'
                    : 'border-gray-200 hover:border-gray-300 bg-white'
                }`}>
                  <input
                    type="checkbox"
                    checked={dateSelectionModal.danceAnalysisRequested || false}
                    onChange={(e) => setDateSelectionModal({
                      ...dateSelectionModal,
                      danceAnalysisRequested: e.target.checked,
                      danceAnalysisDiscipline: e.target.checked ? dateSelectionModal.danceAnalysisDiscipline : ''
                    })}
                    className="w-4 h-4 text-purple-600 rounded mr-3"
                    data-testid="dance-analysis-checkbox"
                  />
                  <span className="text-sm font-medium">Desidero partecipare alla Dance Analysis</span>
                </label>

                {dateSelectionModal.danceAnalysisRequested && (
                  <div className="ml-1">
                    <label className="block text-sm font-semibold text-gray-700 mb-1">
                      Disciplina Dance Analysis *
                    </label>
                    <input
                      type="text"
                      value={dateSelectionModal.danceAnalysisDiscipline || ''}
                      onChange={(e) => setDateSelectionModal({...dateSelectionModal, danceAnalysisDiscipline: e.target.value})}
                      placeholder="Indica la disciplina (es: Latin Style, Hip Hop...)"
                      className={`w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-purple-500 focus:border-purple-500 ${
                        !dateSelectionModal.danceAnalysisDiscipline ? 'border-red-300 bg-red-50' : 'border-green-300 bg-green-50'
                      }`}
                      data-testid="dance-analysis-discipline-input"
                    />
                  </div>
                )}
              </div>
            )}

            <div className="flex gap-3">
              <Button
                onClick={() => setDateSelectionModal(null)}
                variant="outline"
                className="flex-1"
              >
                Annulla
              </Button>
              <Button
                onClick={confirmEnrollWithDates}
                disabled={isMultiDayEvent(dateSelectionModal.event) && dateSelectionModal.selectedDates.length === 0}
                className="flex-1 bg-red-600 hover:bg-red-700 text-white"
                data-testid="confirm-enroll-dates-btn"
              >
                {isMultiDayEvent(dateSelectionModal.event)
                  ? `Iscriviti (${dateSelectionModal.selectedDates.length} ${dateSelectionModal.selectedDates.length === 1 ? 'data' : 'date'})`
                  : 'Iscriviti'}
              </Button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default HomePage;
